<template>
  <SideBar />

  <main id="sidenavmain" class="main-content position-relative max-height-vh-100 h-100 border-radius-lg">
    <NavBar />

    <div class="py-4 container-fluid mb-4">
      <div class="col-lg-12 position-relative mt-n3">

        <div class="ts-head d-flex align-items-center gap-2 mb-3">
          <h6 class="ts-title mb-0">School stamp</h6>
        </div>

        <div class="row g-3">
          <!-- Left: explanation + live preview -->
          <aside class="col-lg-4">
            <div class="card ts-card p-3 mb-3">
              <div class="ts-card-body">
                <p class="fw-bold mb-2">About the stamp</p>
                <p class="text-xs text-muted mb-2">
                  The stamp is the block that prints at the bottom of report forms
                  and fee statements.
                </p>
                <p class="text-xs text-muted mb-2">
                  The values here are <strong>defaults</strong> — when you generate a
                  specific report, you can adjust the date on the spot before
                  printing.
                </p>
                <p class="text-xs text-muted mb-0">
                  The fee statement date is now managed from the
                  <strong>finance</strong> module's stamp page.
                </p>
              </div>
            </div>

            <!-- Live preview -->
            <div class="card ts-card p-3">
              <div class="ts-card-body">
                <p class="fw-bold mb-2">Preview</p>
                <div class="stamp-preview">
                  <div class="stamp-line stamp-title">{{ form.signatoryTitle || '—' }}</div>
                  <div class="stamp-line stamp-school">{{ form.nameLine || '—' }}</div>
                  <div class="stamp-line stamp-secondary" v-if="form.secondaryLine">{{ form.secondaryLine }}</div>
                  <div class="stamp-line stamp-date">{{ form.reportFormDate || '—' }}</div>
                  <div class="stamp-line stamp-dept">{{ form.department || '—' }}</div>
                </div>
                <p class="text-xs text-muted mb-0 mt-2">
                  Shown as it would appear on a report card.
                </p>
              </div>
            </div>
          </aside>

          <!-- Right: form -->
          <section class="col-lg-8">
            <div class="card ts-card p-3">
              <div class="ts-card-body">

                <div v-if="isLoading" class="row g-3">
                  <div v-for="n in 6" :key="n" class="col-md-6">
                    <div class="ts-skel" style="width:120px;height:10px;margin-bottom:6px"></div>
                    <div class="ts-skel" style="height:38px"></div>
                  </div>
                </div>

                <form v-else @submit.prevent="save">

                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="tw-label">Signatory title</label>
                      <input v-model="form.signatoryTitle" type="text" class="tw-input" maxlength="100" placeholder="e.g. HEADTEACHER" />
                      <p class="text-xs text-muted mb-0 mt-1">
                        The role printed above the name.
                      </p>
                    </div>
                    <div class="col-md-6">
                      <label class="tw-label">Name line</label>
                      <input v-model="form.nameLine" type="text" class="tw-input" maxlength="100" placeholder="School name in caps" />
                      <p class="text-xs text-muted mb-0 mt-1">
                        Typically your school name, in capitals.
                      </p>
                    </div>

                    <div class="col-md-6">
                      <label class="tw-label">Secondary line (optional)</label>
                      <input v-model="form.secondaryLine" type="text" class="tw-input" maxlength="100" placeholder="Secondary descriptor or phone" />
                      <p class="text-xs text-muted mb-0 mt-1">
                        Leave blank if not needed. Some schools use this for a phone number
                        or sub-school label.
                      </p>
                    </div>
                    <div class="col-md-6">
                      <label class="tw-label">Department</label>
                      <input v-model="form.department" type="text" class="tw-input" maxlength="100" placeholder="ACCOUNTS DEPARTMENT" />
                    </div>

                    <div class="col-12"><hr class="my-1" /></div>

                    <div class="col-md-6">
                      <label class="tw-label">
                        Report form date
                        <span class="stamp-owner-tag stamp-owner-exam">academics</span>
                      </label>
                      <input v-model="form.reportFormDate" type="text" class="tw-input" maxlength="100" placeholder="e.g. 1st APR 2026" />
                      <p class="text-xs text-muted mb-0 mt-1">
                        Prints on report cards. Set by the exam admin.
                      </p>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end mt-3">
                    <button type="submit" class="tw-btn tw-btn-primary" :disabled="isSaving">
                      {{ isSaving ? 'Saving...' : 'Save stamp' }}
                    </button>
                  </div>

                </form>

              </div>
            </div>
          </section>
        </div>

      </div>
    </div>
    <AppFooter />
  </main>

</template>

<script>
/* eslint-disable */
import SideBar from "@/fragments/exam/AcademicsSidebar.vue";
import NavBar from "@/fragments/exam/AcademicsNavBar.vue";
import AppFooter from "@/fragments/Footer.vue";
import apiService from '@/apis/apiService';
import swal from 'sweetalert';

export default {
  name: "SchoolStampPage",
  components: { SideBar, NavBar, AppFooter },

  data() {
    return {
      isLoading: true,
      isSaving: false,

      form: {
        signatoryTitle: '',
        nameLine: '',
        reportFormDate: '',
        secondaryLine: '',
        department: '',
      },
    };
  },

  mounted() {
    this.fetchStamp();
    if (window.innerWidth >= 1024) {
      import('perfect-scrollbar').then((m) => {
        const PS = m.default;
        const c = document.getElementById('sidenavmain');
        if (c) new PS(c);
      });
    }
  },

  methods: {
    async fetchStamp() {
      this.isLoading = true;
      try {
        const res = await apiService.apiClient.get('/api/academics/stamp');
        Object.assign(this.form, res.data || {});
      } catch (e) {
        swal("Error", this.readErr(e, "Could not load stamp"), "error");
      } finally {
        this.isLoading = false;
      }
    },

    async save() {
      this.isSaving = true;
      try {
        const res = await apiService.apiClient.post('/api/academics/stamp', this.form);
        // Server echoes the normalised row — refresh local state so
        // the preview reflects any trimming the server performed.
        Object.assign(this.form, res.data || {});
        swal("Saved", "Stamp saved", "success");
      } catch (e) {
        swal("Error", this.readErr(e, "Could not save stamp"), "error");
      } finally {
        this.isSaving = false;
      }
    },

    readErr(e, fb) { return (e?.response?.data?.querystatus) || fb; },
  },
};
</script>

<style scoped src="@/assets/css/ts-table.css"></style>

<style scoped>
/* Live preview of the stamp layout — approximates the printed output. */
.stamp-preview {
  background: #fff;
  border: 1px dashed rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  padding: 14px 12px;
  font-family: "Times New Roman", Times, serif;
  line-height: 1.4;
  text-align: center;
  min-height: 140px;
}
.stamp-line {
  font-size: 12px;
  color: #1a1c2e;
}
.stamp-title {
  font-weight: 700;
  text-transform: uppercase;
}
.stamp-school {
  font-weight: 600;
  margin-top: 2px;
}
.stamp-secondary {
  font-size: 11px;
  color: #64748b;
}
.stamp-date {
  margin-top: 10px;
  font-size: 11px;
  color: #64748b;
}
.stamp-dept {
  margin-top: 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

/* Owner tag on the report-form date label */
.stamp-owner-tag {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 10px;
  margin-left: 6px;
  vertical-align: middle;
}
.stamp-owner-exam {
  background: rgba(var(--theme-info-rgb), 0.1);
  color: var(--theme-info);
}
</style>
